import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { STATUS_TEXT, Thread } from "@/components/SupportPanel";
import { StateIdBox } from "@/components/IdentityPanel";

export const IdentityDialog = ({ customer }) => {
  const [info, setInfo] = useState(null);
  const [ssn, setSsn] = useState(null);
  const load = () => api.get(`/admin/customers/${customer.id}/identity`).then((r) => setInfo(r.data)).catch((e) => toast.error(formatApiError(e)));
  const reveal = async () => {
    try {
      setSsn((await api.post(`/admin/customers/${customer.id}/ssn/reveal`)).data.ssn);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog onOpenChange={(o) => { setSsn(null); if (o) load(); }}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`admin-identity-btn-${customer.id}`}>Identity</Button></DialogTrigger>
      <DialogContent data-testid="admin-identity-dialog">
        <DialogHeader><DialogTitle>{customer.name} · identity</DialogTitle></DialogHeader>
        {!info ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="space-y-4 text-sm">
            <div>
              <p className="font-semibold">Social Security number</p>
              {info.ssn_on_file ? (
                <div className="mt-1 flex items-center gap-3">
                  <span className="font-mono" data-testid="admin-ssn-value">{ssn || `***-**-${info.ssn_last4}`}</span>
                  {!ssn && <Button size="sm" variant="ghost" onClick={reveal} data-testid="admin-ssn-reveal-btn"><Eye className="mr-1 h-4 w-4" /> Reveal</Button>}
                </div>
              ) : <p className="text-slate-500" data-testid="admin-ssn-missing">Not provided</p>}
              {!!info.reveal_log.length && <p className="mt-1 text-xs text-slate-500" data-testid="admin-ssn-log">Revealed {info.reveal_log.length} time(s), last by {info.reveal_log[0].admin_email} on {fmtDate(info.reveal_log[0].at)}</p>}
            </div>
            <div>
              {!!info.state_ids.length && <p className="mb-1 text-xs text-slate-500">{info.state_ids.length} ID file(s) on file; newest shown.</p>}
              <StateIdBox info={info} onChanged={load} forUserId={customer.id} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ReplyDialog = ({ r, onDone }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const send = async (close) => {
    try {
      await api.post(`/admin/support/${r.id}/reply`, { message: text, close });
      setText("");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`admin-support-open-${r.id}`}>Open</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="admin-support-dialog">
        <DialogHeader><DialogTitle>{r.subject}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">{r.user_name} · {r.user_email}{r.policy_number && ` · Policy ${r.policy_number}`}</p>
        <Thread r={r} mine="team" />
        <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply to the customer…" data-testid="admin-support-reply-input" />
        <div className="flex gap-2">
          <Button disabled={!text.trim()} onClick={() => send(false)} className="bg-blue-700 text-white hover:bg-blue-800" data-testid="admin-support-reply-btn">Send reply</Button>
          <Button variant="outline" onClick={() => send(true)} data-testid="admin-support-close-btn">{text.trim() ? "Reply & close" : "Close request"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AdminSupport = ({ search }) => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/support").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const s = search.toLowerCase();
  const list = rows.filter((r) => !s || [r.subject, r.user_name, r.user_email, r.policy_number].some((v) => v.toLowerCase().includes(s)));
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full text-sm" data-testid="admin-support-table">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
          <tr>{["Updated", "Customer", "Subject", "Policy", "Status", ""].map((h) => <th key={h} className="whitespace-nowrap p-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {list.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid="admin-support-row">
              <td className="whitespace-nowrap p-3">{fmtDate(r.updated_at)}</td>
              <td className="whitespace-nowrap p-3"><p className="font-semibold">{r.user_name}</p><p className="text-xs text-slate-500">{r.user_email}</p></td>
              <td className="p-3">{r.subject}</td>
              <td className="whitespace-nowrap p-3 font-mono">{r.policy_number || "—"}</td>
              <td className={`whitespace-nowrap p-3 ${r.status === "open" ? "font-semibold text-amber-700" : ""}`}>{r.status === "open" ? "Needs reply" : STATUS_TEXT[r.status]}</td>
              <td className="whitespace-nowrap p-3"><ReplyDialog r={r} onDone={load} /></td>
            </tr>
          ))}
          {!list.length && <tr><td colSpan={6} className="p-6 text-center text-slate-500" data-testid="admin-support-empty">No messages yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};
